import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_DRUG_PROFILE_GROUP_TABLE_KEY as DRUG_PROFILE,
} from "@frontend/common";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { useEntryDataValue } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { EXPEDITED_PROGRAM } from "../constants/expeditedProgramKey.constants";

import type {
  ArticleEntryData,
  ArticleSection,
} from "@/types/articleEntryData.types";

interface UseGetNumberOfGroupShellReturn {
  groupShellCount: (groupShellKey: string | null, tableIndex: number) => number;
}

const GROUP_SHELL_PATH_TO_COUNT_GROUP_SHELL: Record<
  string,
  {
    [key: string]: (data: ArticleEntryData, tableIndex: number) => number;
  }
> = {
  "Clinical Trials": {
    "Endpoints Table Information": (
      data: ArticleEntryData,
      tableIndex: number,
    ) =>
      data?.["Clinical Trials"]?.["Clinical Endpoints (Table)"]?.[tableIndex]?.[
        "Endpoints Table Information"
      ]?.length || 1,
  },
  "Clinical Trial Status": {},
  [SECTION_KEYS.companyWebsiteVariousPages]: {},
  Regulatory: {
    "Approval Process": (data: ArticleEntryData) =>
      data?.Regulatory?.["Approval Process Contents"]?.["Approval Process"]
        ?.length || 1,
  },
  [EXPEDITED_PROGRAM]: {},
  [SECTION_KEYS.deals]: {
    "Rights Scope": (data: ArticleEntryData, tableIndex: number) =>
      data?.[SECTION_KEYS.deals]?.["Licensee Table"]?.[tableIndex][
        "Rights Scope"
      ]?.length || 1,
    "Drug Profile": (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.deals]?.["Drug Profile"]?.length || 1,
  },
  [SECTION_KEYS.dealsLaSc]: {
    "Drug Profile": (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.dealsLaSc]?.["Drug Profile"]?.length || 1,
    "Main Financial Terms": (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.dealsLaSc]?.["Main Financial Terms Table"]?.length ||
      1,
  },
  [SECTION_KEYS.dealFiling]: {
    "Rights Scope": (data: ArticleEntryData, tableIndex: number) =>
      data?.[SECTION_KEYS.dealFiling]?.["Licensee Table"]?.[tableIndex][
        "Rights Scope"
      ]?.length || 1,
    "Drug Profile": (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.dealFiling]?.["Drug Profile"]?.length || 1,
  },
  [SECTION_KEYS.ventureCapital]: {
    "Drug Profile": (data: ArticleEntryData) =>
      data?.Deals?.["Drug Profile"]?.length || 1,
  },
  [SECTION_KEYS.fundingFiling]: {
    "Drug Profile": (data: ArticleEntryData) =>
      data?.Deals?.["Drug Profile"]?.length || 1,
  },
  [SECTION_KEYS.publication]: {
    [DRUG_PROFILE]: (data: ArticleEntryData) =>
      data?.Publication?.[DRUG_PROFILE]?.length || 1,
  },
  [SECTION_KEYS.publicationJournal]: {
    [DRUG_PROFILE]: (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.publicationJournal]?.[DRUG_PROFILE]?.length || 1,
  },
  [SECTION_KEYS.conferenceSchedule]: {
    [DRUG_PROFILE]: (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.conferenceSchedule]?.[DRUG_PROFILE]?.length || 1,
  },
  [SECTION_KEYS.ctg]: {
    [DRUG_PROFILE]: (data: ArticleEntryData) =>
      data?.[SECTION_KEYS.ctg]?.[DRUG_PROFILE]?.length || 1,
  },
};

export const useGetNumberOfGroupShell = (
  articleSectionKey?: ArticleSection,
): UseGetNumberOfGroupShellReturn => {
  const articleSectionByClassification = useArticleSection();
  const articleSection = articleSectionKey || articleSectionByClassification;
  const entryData = useEntryDataValue(getPressIdFromURL());

  return {
    groupShellCount: (groupShellKey: string | null, tableIndex: number) =>
      groupShellKey &&
      GROUP_SHELL_PATH_TO_COUNT_GROUP_SHELL[articleSection][groupShellKey]
        ? GROUP_SHELL_PATH_TO_COUNT_GROUP_SHELL[articleSection][groupShellKey](
            entryData,
            tableIndex,
          ) || 1
        : 1,
  };
};
